/**
 * 绑定邮箱面板。三屏：填邮箱 → 填验证码 → 设密码。
 *
 * ★ 为什么要有验证码这一步：以前一步绑定、完全不验证邮箱，填错的人当场无感 ——
 *   等他忘了密码那天，发信端点为了防枚举永远回 200，他会看到「信已发出」然后永远收不到。
 *   恢复码下线之后，那就是永久丢号。先证明这个邮箱真的收得到信，再让它成为唯一的找回路径。
 *
 * ★ 为什么是验证码不是验证链接：点链接会把用户扔进另一个浏览器上下文
 *   （微信 WebView 里的邮件 → 系统浏览器），填了一半的表单就回不去了。
 *   6 位数字是让他切到邮箱 app 看一眼、切回来手输，上下文不换。
 *
 * ★ 验证码用**一个**输入框而不是 6 个格子：`autocomplete="one-time-code"` 在单框上
 *   才能触发 iOS 的键盘建议栏，拆成 6 格反而破坏自动填充。
 *
 * ★ 改绑（账号已经绑过邮箱）要多填一个「当前密码」。不验的话，拿到一枚被盗 access token
 *   的人把账号绑到自己邮箱，原主人的旧邮箱当场失效，而他没有任何找回路径。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { PublicAccount } from '@poker/shared'

import { IconAlert } from '../lobby/lobbyIcons.js'
import {
  EmailAuthError,
  PASSWORD_MIN_CHARS,
  confirmVerifyCode,
  requestVerifyCode,
} from './emailAuthApi.js'
import { IconKey } from '../icons/index.js'
import { SheetShell } from './sheetShell.js'
import { clearPendingBind, loadPendingBind, savePendingBind } from './pendingBind.js'
import './account.css'

export type EmailBindStage = 'email' | 'code' | 'password' | 'done'

/** 重发冷却。与服务端每设备 5 次/30 分那道闸无关，这只是防连点的手感 */
const RESEND_COOLDOWN_MS = 60_000
/** 与服务端 EMAIL_VERIFY_TTL_MS 对齐；只用来决定「落盘的那条还算不算数」 */
const CODE_TTL_MS = 10 * 60_000

export interface EmailBindSheetProps {
  /** 已经绑过邮箱：文案换成「改绑」，并且要多问一次当前密码 */
  readonly bound: boolean
  /** 当前账号 id。落盘的待验状态要认它——换过身份之后那枚码按旧账号签，对不上 */
  readonly accountId?: string
  onClose?(): void
  onBoundChange?(bound: boolean): void
  /** 注入点：测试用；默认打真接口 */
  readonly sendCode?: (email: string) => Promise<void>
  readonly confirm?: (
    email: string,
    code: string,
    password: string,
    currentPassword?: string,
  ) => Promise<PublicAccount>
  readonly autoFocus?: boolean
  /** 初始阶段：测试用 SSR 静态渲染每一屏 */
  readonly initialStage?: EmailBindStage
  /** 注入时钟：测试用 */
  readonly now?: () => number
}

export function EmailBindSheet({
  bound,
  accountId = '',
  onClose,
  onBoundChange,
  sendCode,
  confirm,
  autoFocus = false,
  initialStage,
  now = () => Date.now(),
}: EmailBindSheetProps): ReactNode {
  // ★ 首次渲染体内同步读回落盘的待验状态：放 effect 里会先闪一帧第一屏，
  //   而用户刚从邮箱 app 切回来，那一帧正好告诉他「你刚才那步白做了」
  const [restored] = useState(() => (accountId === '' ? null : loadPendingBind(accountId, now())))
  const [stage, setStage] = useState<EmailBindStage>(initialStage ?? (restored ? 'code' : 'email'))
  const [email, setEmail] = useState(restored?.email ?? '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [again, setAgain] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resendAt, setResendAt] = useState(restored?.resendAt ?? 0)
  const [tick, setTick] = useState(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // 冷却期间每秒重绘一次，好让按钮上的秒数走字
  useEffect(() => {
    if (resendAt <= now()) return undefined
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [resendAt, now])
  void tick

  const cooldownLeft = Math.max(0, Math.ceil((resendAt - now()) / 1000))

  const fail = useCallback((e: unknown, fallback: string): void => {
    setBusy(false)
    setError(e instanceof EmailAuthError || e instanceof Error ? e.message : fallback)
  }, [])

  const send = useCallback((): void => {
    if (email.trim() === '') {
      setError('请输入邮箱')
      return
    }
    setBusy(true)
    setError(null)
    const call = sendCode ?? ((e: string) => requestVerifyCode(e))
    void call(email).then(
      () => {
        if (!mounted.current) return
        const at = now()
        setBusy(false)
        setStage('code')
        setCode('')
        setResendAt(at + RESEND_COOLDOWN_MS)
        if (accountId !== '') {
          savePendingBind({
            accountId,
            email: email.trim(),
            expiresAt: at + CODE_TTL_MS,
            resendAt: at + RESEND_COOLDOWN_MS,
          })
        }
      },
      (e: unknown) => {
        if (mounted.current) fail(e, '没能发出去，再试一次')
      },
    )
  }, [email, sendCode, accountId, now, fail])

  const toPassword = useCallback((): void => {
    if (!/^[0-9]{6}$/.test(code)) {
      setError('验证码是 6 位数字')
      return
    }
    setError(null)
    setStage('password')
  }, [code])

  const finish = useCallback((): void => {
    if (password.length < PASSWORD_MIN_CHARS) {
      setError(`密码至少 ${String(PASSWORD_MIN_CHARS)} 位`)
      return
    }
    if (password !== again) {
      setError('两次输入的密码不一样')
      return
    }
    if (bound && currentPassword === '') {
      setError('请输入当前密码')
      return
    }
    setBusy(true)
    setError(null)
    const call =
      confirm ??
      ((e: string, c: string, p: string, cur?: string) => confirmVerifyCode(e, c, p, cur))
    void call(email, code, password, bound ? currentPassword : undefined).then(
      () => {
        if (!mounted.current) return
        setBusy(false)
        setStage('done')
        // 明文不在内存里多留一秒
        setPassword('')
        setAgain('')
        setCurrentPassword('')
        setCode('')
        clearPendingBind()
        onBoundChange?.(true)
      },
      (e: unknown) => {
        if (!mounted.current) return
        fail(e, '没能绑定，再试一次')
        // ★ 验证码这一类失败要退回**填码**那一屏，不能停在设密码屏：
        //   用户要么再输一次，要么重发一封，两个动作都在那一屏上
        setStage('code')
      },
    )
  }, [password, again, bound, currentPassword, confirm, email, code, onBoundChange, fail])

  return (
    <SheetShell
      title={bound ? '改绑邮箱' : '绑定邮箱'}
      icon={<IconKey size={20} />}
      dismissible={!busy}
      {...(onClose === undefined ? {} : { onClose })}
    >
      {error !== null && (
        <div className="lobby-error" role="alert">
          <IconAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      {stage === 'email' && (
        <>
          <p className="acct-say">
            {bound
              ? '换一个邮箱。我们先给新邮箱发一个验证码，确认收得到再换。'
              : '换设备之后回到这个账号，靠的就是这个邮箱。先发一个验证码确认它是对的。'}
          </p>
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-bind-email">
              邮箱
            </label>
            <input
              id="email-bind-email"
              className="acct-input acct-input--text"
              type="email"
              name="email"
              value={email}
              disabled={busy}
              autoFocus={autoFocus}
              autoComplete="username"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@example.com"
              onChange={(e) => {
                setEmail(e.target.value)
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') send()
              }}
            />
          </div>
          <p className="acct-status" aria-live="polite">
            {busy ? '正在发送…' : ''}
          </p>
          <button type="button" className="lobby-cta pressable" disabled={busy} onClick={send}>
            发送验证码
          </button>
        </>
      )}

      {stage === 'code' && (
        <>
          {/* ★ 这里可以直说邮箱 —— 是用户自己刚输进去的，不涉及任何存在性泄漏 */}
          <p className="acct-say">
            验证码发到了 <span className="code">{email.trim()}</span>，10 分钟内有效。没收到就看看垃圾箱。
          </p>
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-bind-code">
              验证码
            </label>
            <input
              id="email-bind-code"
              // 基础 .acct-input 本来就是「像验证码」的居中加粗，不加 --text 这个覆盖
              className="acct-input"
              type="text"
              name="one-time-code"
              value={code}
              disabled={busy}
              autoFocus={autoFocus}
              inputMode="numeric"
              // ★ iOS/Android 的一次性验证码自动填充认这个值，且只在**单个**输入框上生效
              autoComplete="one-time-code"
              autoCorrect="off"
              spellCheck={false}
              maxLength={6}
              placeholder="6 位数字"
              onChange={(e) => {
                setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') toPassword()
              }}
            />
          </div>
          <button type="button" className="lobby-cta pressable" disabled={busy} onClick={toPassword}>
            下一步
          </button>
          <div className="acct-actions">
            <button
              type="button"
              className="lobby-btn lobby-btn--ghost"
              disabled={busy || cooldownLeft > 0}
              onClick={send}
            >
              {cooldownLeft > 0 ? `${String(cooldownLeft)} 秒后可重发` : '重新发一封'}
            </button>
            <button
              type="button"
              className="lobby-btn lobby-btn--ghost"
              disabled={busy}
              onClick={() => {
                clearPendingBind()
                setStage('email')
                setError(null)
              }}
            >
              换个邮箱
            </button>
          </div>
        </>
      )}

      {stage === 'password' && (
        <>
          {bound && (
            <div className="acct-field">
              <label className="acct-field__label" htmlFor="email-bind-current">
                当前密码
              </label>
              <input
                id="email-bind-current"
                className="acct-input acct-input--text"
                type="password"
                name="current-password"
                value={currentPassword}
                disabled={busy}
                autoComplete="current-password"
                placeholder="改绑需要确认是你本人"
                onChange={(e) => {
                  setCurrentPassword(e.target.value)
                  if (error !== null) setError(null)
                }}
              />
            </div>
          )}
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-bind-password">
              {bound ? '新密码' : '密码'}
            </label>
            <input
              id="email-bind-password"
              className="acct-input acct-input--text"
              type="password"
              name="new-password"
              value={password}
              disabled={busy}
              autoFocus={autoFocus && !bound}
              autoComplete="new-password"
              placeholder={`至少 ${String(PASSWORD_MIN_CHARS)} 位`}
              onChange={(e) => {
                setPassword(e.target.value)
                if (error !== null) setError(null)
              }}
            />
          </div>
          <div className="acct-field">
            <label className="acct-field__label" htmlFor="email-bind-again">
              再输一遍
            </label>
            <input
              id="email-bind-again"
              className="acct-input acct-input--text"
              type="password"
              name="new-password-again"
              value={again}
              disabled={busy}
              autoComplete="new-password"
              placeholder="确认密码"
              onChange={(e) => {
                setAgain(e.target.value)
                if (error !== null) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') finish()
              }}
            />
          </div>
          <p className="acct-status" aria-live="polite">
            {busy ? '正在绑定…' : ''}
          </p>
          <button type="button" className="lobby-cta pressable" disabled={busy} onClick={finish}>
            {bound ? '完成改绑' : '完成绑定'}
          </button>
        </>
      )}

      {stage === 'done' && (
        <>
          <p className="acct-status acct-status--ok" role="status">
            绑好了，换设备时用这个邮箱和密码登录就行
          </p>
          {/* 改绑撤掉了其它设备的登录态，这是不可逆的，说一次；首次绑定什么都没动，不说 */}
          {bound && <p className="acct-say">其它设备已经登出，需要的话用新邮箱重新登录。</p>}
          <button type="button" className="lobby-cta pressable" onClick={() => onClose?.()}>
            回大厅
          </button>
        </>
      )}
    </SheetShell>
  )
}
